import React, { useEffect, useRef, useState } from "react";
import * as d3 from "d3";
import {
  Box,
  TextField,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Select,
  MenuItem,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { auth, db } from "../../firebase/firebase.js"; // Added db
import { collection, onSnapshot } from "firebase/firestore"; // Added collection, onSnapshot
import {
  // fetchUserProfileKG, // Will be removed
  updateUserProfileKG,
  deleteProfileFact,
} from "../../utils/kgUtils.js";

const slug = (s) =>
  String(s || "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_|_$/g, "");

const categorizePredicate = (pred) => {
  if (!pred) return null;
  if (pred.startsWith("exercise_")) return "Exercise";
  if (pred.startsWith("pref_sleep") || pred.startsWith("sleep_"))
    return "Sleep";
  if (pred.startsWith("diet")) return "Diet";
  if (pred.startsWith("allergy")) return "Allergy";
  if (pred.startsWith("academic_major") || pred.startsWith("career")) return "Education & Career";
  return null;
};

// processNodeValue helper function
const processNodeValue = (currentValue, currentDisplayKey, currentId, originalFact, collapsed, slug) => {
  if (Array.isArray(currentValue)) {
    return currentValue.map((item, index) => {
      const itemId = `${currentId}_${index}`;
      const itemDisplayKey = `${currentDisplayKey}[${index}]`;
      if (typeof item === "object" && item !== null) {
        const childrenData = processNodeValue(item, itemDisplayKey, itemId, originalFact, collapsed, slug);
        return {
          id: itemId,
          label: itemDisplayKey,
          type: "factGroup",
          fact: originalFact,
          childrenData,
          children: collapsed[itemId] ? [] : childrenData,
        };
      } else {
        return {
          id: itemId,
          label: `${itemDisplayKey}: ${item}`,
          type: "factDetail",
          fact: originalFact,
          key: index,
          value: item,
        };
      }
    });
  } else if (typeof currentValue === "object" && currentValue !== null) {
    return Object.keys(currentValue).map((k) => {
      const propertyId = `${currentId}_${slug(k)}`;
      const propertyDisplayKey = k;
      if (typeof currentValue[k] === "object" && currentValue[k] !== null) {
        const childrenData = processNodeValue(currentValue[k], propertyDisplayKey, propertyId, originalFact, collapsed, slug);
        return {
          id: propertyId,
          label: propertyDisplayKey,
          type: "factGroup",
          fact: originalFact,
          childrenData,
          children: collapsed[propertyId] ? [] : childrenData,
        };
      } else {
        return {
          id: propertyId,
          label: `${propertyDisplayKey}: ${currentValue[k]}`,
          type: "factDetail",
          fact: originalFact,
          key: k,
          value: currentValue[k],
        };
      }
    });
  }
  return []; // Return empty array if not an object or array
};

export default function ProfileKGGraph({ uid: propUid }) {
  const currentUser = auth.currentUser?.uid;
  const graphUid = propUid || currentUser;
  const isOwnProfile = !propUid || propUid === currentUser;
  const svgRef = useRef(null);
  const [facts, setFacts] = useState([]);
  const [collapsed, setCollapsed] = useState({});
  const [selectedFact, setSelectedFact] = useState(null);
  const [newPred, setNewPred] = useState("");
  const [newObj, setNewObj] = useState("");
  const [newSubj, setNewSubj] = useState("");
  const [editSubject, setEditSubject] = useState("");
  const [editPredicate, setEditPredicate] = useState("");
  const [editObject, setEditObject] = useState("");
  const [selectedDetail, setSelectedDetail] = useState(null);
  const [editDetailValue, setEditDetailValue] = useState("");
  const theme = useTheme();

  useEffect(() => {
    if (!graphUid) return;

    const profileKGColRef = collection(db, "users", graphUid, "profileKG");

    const unsubscribe = onSnapshot(profileKGColRef, (snapshot) => {
      const newFacts = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
      setFacts(newFacts);
    }, (error) => {
      console.error("Error fetching profileKG snapshot:", error);
      // Optionally, set an error state here
    });

    // Cleanup listener on component unmount or when graphUid changes
    return () => unsubscribe();
  }, [graphUid]);

  useEffect(() => {
    if (selectedFact) {
      setEditSubject(selectedFact.subject || "me");
      setEditPredicate(selectedFact.predicate);
      setEditObject(selectedFact.object);
    }
  }, [selectedFact]);

  useEffect(() => {
    if (selectedDetail) {
      setEditDetailValue(selectedDetail.value);
    }
  }, [selectedDetail]);

  useEffect(() => {
    const svg = d3.select(svgRef.current);
    const currentTransform = d3.zoomTransform(svgRef.current);
    svg.selectAll("*").remove();

    // add zoom and pan behavior
    const container = svg.append("g");
    const zoomBehavior = d3
      .zoom()
      .scaleExtent([0.5, 2])
      .on("zoom", (event) => container.attr("transform", event.transform));
    svg.call(zoomBehavior);
    svg.call(zoomBehavior.transform, currentTransform);
    if (!facts.length) return;

    svg.style("background", theme.palette.background.paper);

    const buildTreeData = () => {
      const subjects = Array.from(new Set(facts.map((f) => f.subject || "me")));
      const children = subjects.map((s) => {
        const id = slug(s);
        const categoryMap = {};
        const uncategorized = [];

        facts
          .filter((f) => (f.subject || "me") === s)
          .forEach((f) => {
            const cat = categorizePredicate(f.predicate);
            if (cat) {
              if (!categoryMap[cat]) categoryMap[cat] = [];
              categoryMap[cat].push(f);
            } else {
              uncategorized.push(f);
            }
          });

        const factChildren = [];

        const processFacts = (factList, parentPrefix) => {
          factList.forEach((f) => {
            let parsed = null;
            try {
              parsed = JSON.parse(f.object);
            } catch {
              parsed = null;
            }
            // Updated condition to use processNodeValue for objects and arrays
            if (parsed && typeof parsed === "object") {
              const parentId = `${parentPrefix}_${slug(f.predicate)}`;
              // Ensure slug and collapsed are available. processNodeValue is defined outside,
              // but it needs `collapsed` from state and `slug` function.
              // We pass them directly here.
              const subChildren = processNodeValue(parsed, f.predicate, parentId, f, collapsed, slug);
              factChildren.push({
                id: parentId,
                label: f.predicate,
                type: "factGroup",
                fact: f,
                childrenData: subChildren,
                children: collapsed[parentId] ? [] : subChildren,
              });
            } else {
              // Existing logic for primitives or non-JSON parseable strings
              factChildren.push({
                id: `${parentPrefix}_${slug(f.predicate)}`, // Ensure predicate is slugged
                label: `${f.predicate}: ${f.object}`,
                type: "fact", // Remains "fact" as it's a direct value
                fact: f,
              });
            }
          });
        };

        Object.entries(categoryMap).forEach(([cat, list]) => {
          const catId = `${id}_${slug(cat)}`;
          const prevLen = factChildren.length;
          processFacts(list, catId); // processFacts will now use processNodeValue internally
          const inserted = factChildren.splice(prevLen);
          factChildren.push({
            id: catId,
            label: cat,
            type: "category",
            childrenData: inserted,
            children: collapsed[catId] ? [] : inserted,
          });
        });

        processFacts(uncategorized, id); // Same here

        return {
          id,
          label: s === "me" ? "You" : s,
          type: "subject",
          raw: s,
          childrenData: factChildren,
          children: collapsed[id] ? [] : factChildren,
        };
      });
      return { id: "root", label: "You", type: "root", children };
    };

    const root = d3.hierarchy(buildTreeData());

    // Use a fixed width for each depth level
    const LEVEL_WIDTH = 160;
    // Define a fixed vertical spacing for each node.
    // Adjust this value based on your node content's height.
    // Consider text height, padding, and any icons.
    const NODE_VERTICAL_SPACING = 40; // e.g., 30px, 40px, etc.

    // Use nodeSize instead of size
    // For a horizontal tree:
    // - First arg is vertical separation between siblings.
    // - Second arg is horizontal separation between parent/child (depth).
    const tree = d3.tree().nodeSize([NODE_VERTICAL_SPACING, LEVEL_WIDTH]);
    tree(root);

    const nodes = root.descendants();
    const links = root.links();

    const gLinks = container
      .append("g")
      .attr("fill", "none")
      .attr("stroke", theme.palette.text.secondary)
      .selectAll("path")
      .data(links)
      .join("path")
      .attr(
        "d",
        d3
          .linkHorizontal()
          .x((d) => d.y)
          .y((d) => d.x)
      );

    const node = container
      .append("g")
      .selectAll("g")
      .data(nodes)
      .join("g")
      .attr("transform", (d) => `translate(${d.y},${d.x})`);

    node.each(function (d) {
      const g = d3.select(this);
      const text = g
        .append("text")
        .attr("dy", "0.35em")
        .style("font-size", "0.75rem")
        .style("fill", "#000")
        .text(d.data.label);
      const bbox = text.node().getBBox();
      const paddingX = 6;
      const paddingY = 4;
      g.insert("rect", "text")
        .attr("x", bbox.x - paddingX)
        .attr("y", bbox.y - paddingY)
        .attr("width", bbox.width + paddingX * 2)
        .attr("height", bbox.height + paddingY * 2)
        .attr("rx", 4)
        .attr(
          "fill",
          d.data.type === "subject" ? theme.palette.primary.main : "#ffb347"
        );
      if (d.data.childrenData && d.data.childrenData.length) {
        g.append("text")
          .attr("x", bbox.x + bbox.width + paddingX + 2)
          .attr("y", bbox.y - paddingY)
          .attr("font-size", "0.85rem")
          .attr("cursor", "pointer")
          .style("cursor", "pointer")
          .style("fill", theme.palette.text.primary)
          .text(collapsed[d.data.id] ? ">" : "<")
          .on("click", (event) => {
            event.stopPropagation();
            setCollapsed((c) => ({ ...c, [d.data.id]: !c[d.data.id] }));
          });
      }
      if (
        (d.data.type === "fact" ||
          d.data.type === "factGroup" ||
          d.data.type === "factDetail") &&
        isOwnProfile
      ) {
        g.append("text")
          .attr("x", bbox.x + bbox.width + paddingX + 2)
          .attr("y", bbox.y + bbox.height + paddingY)
          .attr("font-size", "0.85rem")
          .attr("cursor", "pointer")
          .style("cursor", "pointer")
          .style("fill", theme.palette.text.primary)
          .text("✎")
          .on("click", (event) => {
            event.stopPropagation();
            if (d.data.type === "factDetail") {
              setSelectedDetail({
                fact: d.data.fact,
                key: d.data.key,
                value: d.data.value,
              });
            } else {
              setSelectedFact(d.data.fact);
            }
          });
      }
    });
  }, [facts, graphUid, theme, collapsed]);

  const addFact = async () => {
    if (!newPred || !newObj) return;
    const factId = newSubj ? `${slug(newSubj)}_${newPred}` : newPred;
    const node = {
      id: factId,
      predicate: newPred,
      object: newObj,
      datatype: "string",
      confidence: 1,
      subject: newSubj || "me",
    };
    await updateUserProfileKG(currentUser, [node]);
    setFacts([...facts, node]);
    setNewPred("");
    setNewObj("");
    setNewSubj("");
  };

  const subjects = Array.from(new Set(facts.map((f) => f.subject || "me")));

  return (
    <Box>
      <Box sx={{ width: "100%", height: "73vh" }}>
        <svg ref={svgRef} width="100%" height="100%" />
      </Box>
      {isOwnProfile && selectedFact && (
        <Dialog open onClose={() => setSelectedFact(null)}>
          <DialogTitle>Edit Fact</DialogTitle>
          <DialogContent sx={{ display: "flex", gap: 1, mt: 1 }}>
            <TextField
              size="small"
              label="Subject"
              value={editSubject}
              onChange={(e) => setEditSubject(e.target.value)}
            />
            <TextField
              size="small"
              label="Predicate"
              value={editPredicate}
              onChange={(e) => setEditPredicate(e.target.value)}
            />
            <TextField
              size="small"
              label="Value"
              value={editObject}
              onChange={(e) => setEditObject(e.target.value)}
            />
          </DialogContent>
          <DialogActions>
            <Button
              onClick={async () => {
                const oldId = selectedFact.id;
                const newId = `${slug(editSubject)}_${editPredicate}`;
                await updateUserProfileKG(currentUser, [
                  {
                    id: newId,
                    subject: editSubject,
                    predicate: editPredicate,
                    object: editObject,
                    datatype: selectedFact.datatype,
                    confidence: selectedFact.confidence,
                  },
                ]);
                if (newId !== oldId) {
                  await deleteProfileFact(currentUser, oldId);
                }
                setFacts((prev) => [
                  ...prev.filter((f) => f.id !== oldId),
                  {
                    ...selectedFact,
                    id: newId,
                    subject: editSubject,
                    predicate: editPredicate,
                    object: editObject,
                  },
                ]);
                setSelectedFact(null);
              }}
              variant="contained"
            >
              Save
            </Button>
            <Button onClick={() => setSelectedFact(null)}>Cancel</Button>
            <Button
              color="error"
              onClick={async () => {
                await deleteProfileFact(currentUser, selectedFact.id);
                setFacts((prev) =>
                  prev.filter((f) => f.id !== selectedFact.id)
                );
                setSelectedFact(null);
              }}
            >
              Delete
            </Button>
          </DialogActions>
        </Dialog>
      )}
      {isOwnProfile && selectedDetail && (
        <Dialog open onClose={() => setSelectedDetail(null)}>
          <DialogTitle>Edit Property</DialogTitle>
          <DialogContent sx={{ display: "flex", gap: 1, mt: 1 }}>
            <TextField
              size="small"
              label="Property"
              value={selectedDetail.key}
              disabled
            />
            <TextField
              size="small"
              label="Value"
              value={editDetailValue}
              onChange={(e) => setEditDetailValue(e.target.value)}
            />
          </DialogContent>
          <DialogActions>
            <Button
              onClick={async () => {
                const { fact, key } = selectedDetail;
                let parsed;
                try {
                  parsed = JSON.parse(fact.object);
                } catch {
                  parsed = {};
                }
                parsed[key] = editDetailValue;
                const updatedObj = JSON.stringify(parsed);
                await updateUserProfileKG(currentUser, [
                  { ...fact, object: updatedObj },
                ]);
                setFacts((prev) =>
                  prev.map((f) =>
                    f.id === fact.id ? { ...f, object: updatedObj } : f
                  )
                );
                setSelectedDetail(null);
              }}
              variant="contained"
            >
              Save
            </Button>
            <Button onClick={() => setSelectedDetail(null)}>Cancel</Button>
          </DialogActions>
        </Dialog>
      )}
      {isOwnProfile && (
        <Box sx={{ mt: 2, display: "flex", gap: 1, alignItems: "center" }}>
          <TextField
            size="small"
            label="Subject"
            value={newSubj}
            onChange={(e) => setNewSubj(e.target.value)}
            data-testid="profile-kg-add-subject-input"
          />
          <TextField
            size="small"
            label="Predicate"
            value={newPred}
            onChange={(e) => setNewPred(e.target.value)}
            data-testid="profile-kg-add-predicate-input"
          />
          <TextField
            size="small"
            label="Value"
            value={newObj}
            onChange={(e) => setNewObj(e.target.value)}
            data-testid="profile-kg-add-value-input"
          />
          <Button variant="contained" onClick={addFact} data-testid="profile-kg-add-fact-button">
            Add Fact
          </Button>
        </Box>
      )}
    </Box>
  );
}
